import React, { useState, useRef, useCallback } from 'react';
import './PNGToTemplateConverter.css';

const PNGToTemplateConverter = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [template, setTemplate] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [frameName, setFrameName] = useState('');
  const [analysisData, setAnalysisData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableTemplate, setEditableTemplate] = useState(null);
  const [error, setError] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState('');
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const timeoutRef = useRef(null);

  // Enhanced file validation
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Clear previous errors
    setError(null);
    
    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('File size too large. Please select a file smaller than 50MB.');
      return;
    }
    
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const isValidType = validTypes.includes(file.type) || file.name.toLowerCase().endsWith('.psd');
    
    if (!isValidType) {
      setError('Please select a valid file: PNG, JPG, or PSD');
      return;
    }
    
    console.log('File selected:', { name: file.name, size: file.size, type: file.type });
    
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setTemplate(null);
    setAnalysisData(null);
    setIsEditing(false);
    setAnalysisProgress('');
  }, []);

  // Enhanced analysis with timeout and error handling
  const analyzeFile = useCallback(async () => {
    if (!selectedFile || !previewUrl) return;

    console.log('Starting analysis...', { file: selectedFile.name, size: selectedFile.size });
    setIsAnalyzing(true);
    setError(null);
    setAnalysisProgress('Initializing analysis...');
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Add timeout to prevent infinite loading
    timeoutRef.current = setTimeout(() => {
      console.log('Analysis timeout reached');
      setIsAnalyzing(false);
      setError('Analysis timed out. Please try again or use a different file.');
      setAnalysisProgress('');
    }, 30000); // 30 second timeout

    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      console.log('Image object created, setting up event handlers...');
      setAnalysisProgress('Loading image...');
      
      img.onload = function() {
        console.log('Image loaded successfully', { width: img.width, height: img.height });
        setAnalysisProgress('Processing image data...');
        
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        console.log('Image drawn to canvas, extracting data...');
        setAnalysisProgress('Extracting colors...');
        
        // Extract image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const colors = extractDominantColors(imageData.data);
        const dimensions = { width: img.width, height: img.height };
        
        console.log('Analysis complete', { colors: colors.length, dimensions });
        setAnalysisProgress('Analysis complete!');
        
        // Clear timeout since analysis completed
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        setAnalysisData({ colors, dimensions });
        setIsAnalyzing(false);
        
        // Clear progress message after a short delay
        setTimeout(() => setAnalysisProgress(''), 2000);
      };
      
      img.onerror = function(error) {
        console.error('Image load error:', error);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setIsAnalyzing(false);
        setError('Error loading image. Please try a different file.');
        setAnalysisProgress('');
      };
      
      console.log('Setting image source...');
      setAnalysisProgress('Loading image...');
      img.src = previewUrl;
    } catch (error) {
      console.error('Error in analysis:', error);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsAnalyzing(false);
      setError('Error analyzing file. Please try again.');
      setAnalysisProgress('');
    }
  }, [selectedFile, previewUrl]);

  // Skip analysis option
  const skipAnalysis = useCallback(() => {
    if (!selectedFile) return;
    
    console.log('Skipping analysis, using default values');
    
    // Create default analysis data
    const defaultAnalysis = {
      colors: ['#ffffff', '#667eea', '#764ba2', '#4CAF50', '#333333', '#666666'],
      dimensions: { width: 800, height: 600 }
    };
    
    setAnalysisData(defaultAnalysis);
    setIsAnalyzing(false);
    setError(null);
    setAnalysisProgress('');
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [selectedFile]);

  // Extract dominant colors from image data
  const extractDominantColors = useCallback((imageData) => {
    const colorCounts = {};
    
    for (let i = 0; i < imageData.length; i += 4) {
      const r = imageData[i];
      const g = imageData[i + 1];
      const b = imageData[i + 2];
      const a = imageData[i + 3];
      
      if (a > 128) { // Only count non-transparent pixels
        const color = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        colorCounts[color] = (colorCounts[color] || 0) + 1;
      }
    }
    
    // Sort by frequency and return top colors
    return Object.entries(colorCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([color]) => color);
  }, []);

  // Generate template JSON
  const generateTemplate = useCallback(() => {
    if (!analysisData || !frameName.trim()) return;

    const { colors, dimensions } = analysisData;
    
    const template = {
      templateId: `${frameName.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`,
      name: frameName,
      sourceImage: selectedFile?.name || 'unknown',
      layout: {
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        backgroundColor: colors[0] || "#ffffff",
        borderRadius: "12px",
        padding: "20px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)"
      },
      elements: {
        logo: {
          position: { top: "3%", left: "3%" },
          size: { width: "15%", minWidth: "60px" },
          content: {
            image: "{{logo_image}}",
            text: "{{logo_text}}",
            alt: "Logo"
          },
          styling: {
            backgroundColor: "transparent",
            border: "none",
            borderRadius: "8px",
            padding: "10px"
          },
          placeholder: {
            backgroundColor: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            color: "#666",
            fontSize: "14px",
            fontWeight: "bold"
          }
        },
        header: {
          position: { top: "4%", left: "20%", right: "15%" },
          content: {
            text: "{{header_text}}",
            fontSize: "clamp(16px, 3vw, 24px)"
          },
          styling: {
            fontSize: "clamp(16px, 3vw, 24px)",
            fontWeight: "bold",
            color: colors[4] || "#333",
            fontFamily: "Arial, sans-serif",
            textAlign: "center"
          },
          placeholder: {
            backgroundColor: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            borderRadius: "8px",
            padding: "8px 12px",
            color: "#666",
            fontSize: "14px",
            fontWeight: "bold"
          }
        },
        socialMedia: {
          position: { top: "3%", right: "3%", display: "flex", gap: "8px" },
          icons: [
            {
              type: "facebook",
              symbol: "f",
              link: "{{facebook_link}}",
              styling: {
                width: "30px",
                height: "30px",
                backgroundColor: "rgba(128, 128, 128, 0.3)",
                border: "2px dashed #999",
                borderRadius: "50%",
                color: "#666",
                fontSize: "12px"
              }
            },
            {
              type: "twitter",
              symbol: "t",
              link: "{{twitter_link}}",
              styling: {
                width: "30px",
                height: "30px",
                backgroundColor: "rgba(128, 128, 128, 0.3)",
                border: "2px dashed #999",
                borderRadius: "50%",
                color: "#666",
                fontSize: "12px"
              }
            },
            {
              type: "instagram",
              symbol: "📷",
              link: "{{instagram_link}}",
              styling: {
                width: "30px",
                height: "30px",
                backgroundColor: "rgba(128, 128, 128, 0.3)",
                border: "2px dashed #999",
                borderRadius: "50%",
                color: "#666",
                fontSize: "12px"
              }
            }
          ]
        },
        mainPoster: {
          position: { top: "15%", left: "5%", right: "5%", height: "50%" },
          content: {
            image: "{{main_poster_image}}",
            text: "{{main_poster_text}}",
            background: "{{main_poster_background}}"
          },
          styling: {
            backgroundColor: "transparent",
            border: "none",
            borderRadius: "8px"
          },
          placeholder: {
            backgroundColor: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            borderRadius: "8px",
            color: "#666",
            fontSize: "18px",
            fontWeight: "bold"
          }
        },
        doctorImage: {
          position: { bottom: "15%", left: "5%" },
          size: { width: "25%", minWidth: "120px" },
          content: {
            image: "{{doctor_image}}",
            alt: "Doctor Photo"
          },
          styling: {
            borderRadius: "10px",
            objectFit: "cover"
          },
          placeholder: {
            backgroundColor: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            borderRadius: "10px",
            width: "120px",
            height: "160px",
            color: "#666",
            fontSize: "12px",
            fontWeight: "bold",
            textAlign: "center"
          }
        },
        doctorInfo: {
          position: { bottom: "20%", left: "32%", right: "25%" },
          styling: {
            display: "flex",
            flexDirection: "column",
            gap: "8px"
          },
          name: {
            content: {
              text: "{{doctor_name}}",
              placeholder: "Dr. John Smith"
            },
            styling: {
              fontSize: "16px",
              fontWeight: "bold",
              color: colors[4] || "#333"
            },
            placeholder: {
              backgroundColor: "rgba(128, 128, 128, 0.3)",
              border: "2px dashed #999",
              borderRadius: "6px",
              padding: "6px 10px",
              color: "#666",
              fontSize: "14px",
              fontWeight: "bold"
            }
          },
          specialization: {
            content: {
              text: "{{doctor_specialization}}",
              placeholder: "Cardiologist"
            },
            styling: {
              fontSize: "14px",
              color: colors[5] || "#666"
            },
            placeholder: {
              backgroundColor: "rgba(128, 128, 128, 0.3)",
              border: "2px dashed #999",
              borderRadius: "6px",
              padding: "6px 10px",
              color: "#666",
              fontSize: "12px"
            }
          },
          phoneNumber: {
            content: {
              text: "{{doctor_phone}}",
              placeholder: "+1-555-0123"
            },
            styling: {
              fontSize: "14px",
              color: colors[5] || "#666"
            },
            placeholder: {
              backgroundColor: "rgba(128, 128, 128, 0.3)",
              border: "2px dashed #999",
              borderRadius: "6px",
              padding: "6px 10px",
              color: "#666",
              fontSize: "12px"
            }
          },
          email: {
            content: {
              text: "{{doctor_email}}",
              placeholder: "doctor@example.com"
            },
            styling: {
              fontSize: "14px",
              color: colors[5] || "#666"
            },
            placeholder: {
              backgroundColor: "rgba(128, 128, 128, 0.3)",
              border: "2px dashed #999",
              borderRadius: "6px",
              padding: "6px 10px",
              color: "#666",
              fontSize: "12px"
            }
          }
        },
        qrCode: {
          position: { bottom: "15%", right: "5%" },
          size: { width: "15%", minWidth: "80px" },
          content: {
            image: "{{qr_code_image}}",
            link: "{{qr_code_link}}",
            alt: "QR Code"
          },
          styling: {
            objectFit: "contain"
          },
          placeholder: {
            backgroundColor: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            borderRadius: "8px",
            width: "80px",
            height: "80px",
            color: "#666",
            fontSize: "10px",
            fontWeight: "bold",
            textAlign: "center"
          }
        },
        footer: {
          position: { bottom: "5%", left: "5%", right: "5%" },
          content: {
            text: "{{footer_text}}",
            background: "{{footer_background}}"
          },
          styling: {
            textAlign: "center"
          },
          placeholder: {
            backgroundColor: "rgba(128, 128, 128, 0.3)",
            border: "2px dashed #999",
            borderRadius: "6px",
            padding: "8px 12px",
            color: "#666",
            fontSize: "12px",
            fontWeight: "bold"
          }
        }
      },
      colorScheme: {
        primary: colors[1] || "#667eea",
        secondary: colors[2] || "#764ba2",
        accent: colors[3] || "#4CAF50",
        text: {
          primary: colors[4] || "#333333",
          secondary: colors[5] || "#666666",
          light: colors[6] || "#999999"
        },
        background: {
          main: colors[0] || "#ffffff",
          secondary: "#f5f5f5",
          accent: "#e3f2fd"
        },
        placeholder: {
          background: "rgba(128, 128, 128, 0.3)",
          border: "#999999",
          text: "#666666"
        }
      },
      typography: {
        fontFamily: {
          primary: "Arial, sans-serif",
          secondary: "Georgia, serif",
          accent: "Monaco, monospace"
        },
        fontSizes: {
          small: "12px",
          medium: "14px",
          large: "16px",
          xlarge: "18px",
          xxlarge: "24px"
        },
        fontWeights: {
          normal: "400",
          medium: "500",
          bold: "700"
        }
      },
      spacing: {
        xs: "4px",
        sm: "8px",
        md: "16px",
        lg: "24px",
        xl: "32px",
        xxl: "48px"
      },
      borders: {
        radius: {
          small: "4px",
          medium: "8px",
          large: "12px",
          round: "50%"
        },
        styles: {
          solid: "solid",
          dashed: "dashed",
          dotted: "dotted"
        }
      },
      dynamicContent: {
        images: [
          "{{logo_image}}",
          "{{main_poster_image}}",
          "{{doctor_image}}",
          "{{qr_code_image}}"
        ],
        texts: [
          "{{header_text}}",
          "{{doctor_name}}",
          "{{doctor_specialization}}",
          "{{doctor_phone}}",
          "{{doctor_email}}",
          "{{footer_text}}"
        ],
        links: [
          "{{facebook_link}}",
          "{{twitter_link}}",
          "{{instagram_link}}",
          "{{qr_code_link}}"
        ],
        backgrounds: [
          "{{main_poster_background}}",
          "{{footer_background}}"
        ]
      }
    };

    setTemplate(template);
  }, [analysisData, frameName, selectedFile]);

  // Start editing template
  const startEditing = useCallback(() => {
    if (!template) return;
    setEditableTemplate(JSON.parse(JSON.stringify(template)));
    setIsEditing(true);
  }, [template]);

  // Save edited template
  const saveEditedTemplate = useCallback(() => {
    if (!editableTemplate) return;
    setTemplate(editableTemplate);
    setIsEditing(false);
  }, [editableTemplate]);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditableTemplate(null);
  }, []);

  // Update editable template
  const updateEditableTemplate = useCallback((path, value) => {
    if (!editableTemplate) return;
    
    const newTemplate = JSON.parse(JSON.stringify(editableTemplate));
    const keys = path.split('.');
    let current = newTemplate;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setEditableTemplate(newTemplate);
  }, [editableTemplate]);

  // Download template JSON
  const downloadTemplate = useCallback(() => {
    if (!template) return;

    const jsonString = JSON.stringify(template, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.templateId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [template]);

  // Reset component
  const resetComponent = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setTemplate(null);
    setAnalysisData(null);
    setIsEditing(false);
    setEditableTemplate(null);
    setError(null);
    setAnalysisProgress('');
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Render editable field
  const renderEditableField = (label, path, type = 'text', placeholder = '') => {
    const value = editableTemplate ? getNestedValue(editableTemplate, path) : '';
    
    return (
      <div className="editable-field">
        <label>{label}:</label>
        {type === 'textarea' ? (
          <textarea
            value={value}
            onChange={(e) => updateEditableTemplate(path, e.target.value)}
            placeholder={placeholder}
            rows="3"
          />
        ) : (
          <input
            type={type}
            value={value}
            onChange={(e) => updateEditableTemplate(path, e.target.value)}
            placeholder={placeholder}
          />
        )}
      </div>
    );
  };

  // Get nested value from object
  const getNestedValue = (obj, path) => {
    const keys = path.split('.');
    let current = obj;
    
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return '';
      }
    }
    
    return current || '';
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="png-converter">
      <div className="converter-header">
        <h2>PSD/PNG to Template Converter</h2>
        <p>Upload PSD, PNG, or JPG files to generate and edit template JSON</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      <div className="converter-content">
        {/* File Upload Section */}
        <div className="upload-section">
          <div className="file-input-wrapper">
            <input
              ref={fileInputRef}
              type="file"
              accept=".psd,.png,.jpg,.jpeg"
              onChange={handleFileSelect}
              className="file-input"
            />
            <button 
              className="upload-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose File (PSD/PNG/JPG)
            </button>
          </div>
          
          {selectedFile && (
            <div className="file-info">
              <span>Selected: {selectedFile.name}</span>
              <button onClick={resetComponent} className="reset-btn">
                Reset
              </button>
            </div>
          )}
        </div>

        {/* Frame Name Input */}
        {selectedFile && (
          <div className="frame-name-section">
            <label htmlFor="frameName">Frame Template Name:</label>
            <input
              id="frameName"
              type="text"
              value={frameName}
              onChange={(e) => setFrameName(e.target.value)}
              placeholder="Enter frame template name"
              className="frame-name-input"
            />
          </div>
        )}

        {/* Preview and Analysis */}
        {previewUrl && (
          <div className="preview-section">
            <div className="image-preview">
              <h3>File Preview</h3>
              <img src={previewUrl} alt="File Preview" className="preview-image" />
              
              {!analysisData && (
                <div className="analysis-buttons">
                  <button 
                    onClick={analyzeFile}
                    disabled={isAnalyzing}
                    className="analyze-btn"
                  >
                    {isAnalyzing ? 'Analyzing...' : 'Analyze File'}
                  </button>
                  
                  <button 
                    onClick={skipAnalysis}
                    className="skip-btn"
                  >
                    Skip Analysis
                  </button>
                </div>
              )}
              
              {/* Analysis Progress */}
              {isAnalyzing && analysisProgress && (
                <div className="analysis-progress">
                  <div className="progress-spinner"></div>
                  <span>{analysisProgress}</span>
                </div>
              )}
            </div>

            {/* Analysis Results */}
            {analysisData && (
              <div className="analysis-results">
                <h3>Analysis Results</h3>
                <div className="analysis-grid">
                  <div className="analysis-item">
                    <label>Dimensions:</label>
                    <span>{analysisData.dimensions.width} x {analysisData.dimensions.height}px</span>
                  </div>
                  <div className="analysis-item">
                    <label>Aspect Ratio:</label>
                    <span>{(analysisData.dimensions.width / analysisData.dimensions.height).toFixed(2)}</span>
                  </div>
                  <div className="analysis-item">
                    <label>Dominant Colors:</label>
                    <div className="color-palette">
                      {analysisData.colors.slice(0, 6).map((color, index) => (
                        <div 
                          key={index} 
                          className="color-swatch"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                
                <button 
                  onClick={generateTemplate}
                  disabled={!frameName.trim()}
                  className="generate-btn"
                >
                  Generate Template JSON
                </button>
              </div>
            )}
          </div>
        )}

        {/* Generated Template */}
        {template && !isEditing && (
          <div className="template-section">
            <div className="template-header">
              <h3>Generated Template</h3>
              <div className="template-actions">
                <button onClick={startEditing} className="edit-btn">
                  Edit Template
                </button>
                <button onClick={downloadTemplate} className="download-btn">
                  Download JSON
                </button>
              </div>
            </div>
            
            <div className="template-preview">
              <pre className="json-preview">
                {JSON.stringify(template, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Editable Template */}
        {isEditing && editableTemplate && (
          <div className="editable-template-section">
            <div className="editable-header">
              <h3>Edit Template</h3>
              <div className="editable-actions">
                <button onClick={saveEditedTemplate} className="save-btn">
                  Save Changes
                </button>
                <button onClick={cancelEditing} className="cancel-btn">
                  Cancel
                </button>
              </div>
            </div>

            <div className="editable-content">
              <div className="editable-section">
                <h4>Basic Information</h4>
                {renderEditableField('Template Name', 'name', 'text', 'Enter template name')}
                {renderEditableField('Background Color', 'layout.backgroundColor', 'color')}
                {renderEditableField('Border Radius', 'layout.borderRadius', 'text', '12px')}
              </div>

              <div className="editable-section">
                <h4>Header Content</h4>
                {renderEditableField('Header Text', 'elements.header.content.text', 'text', 'Enter header text')}
                {renderEditableField('Header Color', 'elements.header.styling.color', 'color')}
                {renderEditableField('Header Font Size', 'elements.header.styling.fontSize', 'text', '24px')}
              </div>

              <div className="editable-section">
                <h4>Doctor Information</h4>
                {renderEditableField('Doctor Name', 'elements.doctorInfo.name.content.text', 'text', 'Dr. John Smith')}
                {renderEditableField('Specialization', 'elements.doctorInfo.specialization.content.text', 'text', 'Cardiologist')}
                {renderEditableField('Phone Number', 'elements.doctorInfo.phoneNumber.content.text', 'text', '+1-555-0123')}
                {renderEditableField('Email', 'elements.doctorInfo.email.content.text', 'text', 'doctor@example.com')}
              </div>

              <div className="editable-section">
                <h4>Images & Media</h4>
                {renderEditableField('Logo Image', 'elements.logo.content.image', 'text', '{{logo_image}}')}
                {renderEditableField('Main Poster Image', 'elements.mainPoster.content.image', 'text', '{{main_poster_image}}')}
                {renderEditableField('Doctor Image', 'elements.doctorImage.content.image', 'text', '{{doctor_image}}')}
                {renderEditableField('QR Code Image', 'elements.qrCode.content.image', 'text', '{{qr_code_image}}')}
              </div>

              <div className="editable-section">
                <h4>Social Media Links</h4>
                {renderEditableField('Facebook Link', 'elements.socialMedia.icons.0.link', 'text', '{{facebook_link}}')}
                {renderEditableField('Twitter Link', 'elements.socialMedia.icons.1.link', 'text', '{{twitter_link}}')}
                {renderEditableField('Instagram Link', 'elements.socialMedia.icons.2.link', 'text', '{{instagram_link}}')}
              </div>

              <div className="editable-section">
                <h4>Footer & Additional</h4>
                {renderEditableField('Footer Text', 'elements.footer.content.text', 'text', '{{footer_text}}')}
                {renderEditableField('Footer Background', 'elements.footer.content.background', 'text', '{{footer_background}}')}
                {renderEditableField('QR Code Link', 'elements.qrCode.content.link', 'text', '{{qr_code_link}}')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden canvas for image analysis */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default PNGToTemplateConverter;
